import { FC, memo, useMemo, useEffect, useCallback, useState } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router';
import { Share2 } from 'lucide-react';

import { APP_ROUTES } from '@navigation/types';
import { useScreenSizes } from '@hooks/useScreenSizes';
import { AlertType } from '@utils/constants';
import { useModal } from '@hooks/useModal';
import { useAgoraEngine } from '@providers/AgoraEngineProvider';
import { useAlert } from '@providers/AlertProvider';
// import { useCurrentUserParticipantData } from '@redux/selectors/participants';
import { useParticipantRequestToSpeak } from '@redux/selectors/requestsToSpeak';
import { useShowDataById } from '@redux/selectors/shows';
import { useShareModal } from '@hooks/useShareModal';
import { generateShowShareData } from '@utils/share';
import {
  useLeftTeamSpeakerId,
  useRightTeamSpeakerId,
  useSpeakerDataById,
} from '@redux/selectors/speakers';
import { useAuthUser } from '@redux/selectors/auth';
import { useTopicDataById } from '@redux/selectors/topics';
import { requestsToSpeakSlice } from '@redux/state/requestsToSpeakSlice';
import { chatSlice } from '@redux/state/chatSlice';
import { usersSlice } from '@redux/state/usersSlice';
import { useAppDispatch } from '@redux/store';
import { RequestToSpeakStatus } from '@typings/requestToSpeak';
import { ShowStatus, ShowType, ChatMessageServerData } from '@typings/show';
import theme from 'theme';
import Loader from '@components/loader';
import FullScreenLoader from '@components/loader/FullScreenLoader';
import Modal from '@components/modal';
import Sides from '@components/show/card/components/Sides';
import Menu from '@components/show/menu';
import Participants from '@components/show/participants';
import { DefaultText, PremierText } from '@components/text';
import DynamicChat from '@components/show/dynamicChat';
import MobileBottomMenu from '@components/show/mobileBottomMenu';
import MobileChat from '@components/show/mobileChat';
import backgroundImage from '@assets/images/card.png';

import socket from '../../services/socket.service';

const Container = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  background-color: ${({ theme }) => theme.colors.black};
`;

const Header = styled.div<{ $backgroundImage?: string }>`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  position: relative;
  background-image: ${({ $backgroundImage }) =>
    $backgroundImage ? `url(${$backgroundImage})` : 'none'};
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: ${({ theme }) =>
      `${theme.colors.black}${theme.opacity[60]}`};
    z-index: 1;
  }

  > * {
    position: relative;
    z-index: 2;
  }
`;

const MenuContainer = styled.div`
  width: 100%;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  padding: 8px 16px;
  gap: 8%;
`;

const TitleContainer = styled.div`
  width: 100%;
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  justify-content: space-between;
  padding: 8px 16px;
  gap: 32px;
`;

const Title = styled(PremierText)`
  flex: 1;
  font-size: ${({ theme }) => theme.fonts.fontSize['2xl']};
  color: ${({ theme }) => theme.colors.white};
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    font-size: ${({ theme }) => theme.fonts.fontSize.xl};
  }
`;

const ShareButton = styled(Share2)`
  width: 24px;
  height: 24px;
  color: ${({ theme }) => theme.colors.white};

  &:hover {
    cursor: pointer;
    opacity: 0.8;
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    width: 20px;
    height: 20px;
  }
`;

const Body = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  overflow: hidden;

  @media (max-width: ${({ theme }) => theme.breakpoints.lg}) {
    padding-bottom: 80px;
  }
`;

const MainContent = styled.div`
  width: 100%;
  flex: 1;
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  overflow: hidden;

  @media (max-width: ${({ theme }) => theme.breakpoints.lg}) {
    flex-direction: column;
  }
`;

const Content = styled.div`
  position: relative;
  width: 60%;
  height: calc(100% - 96px);
  display: flex;
  flex-direction: column;
  align-items: flex-start;

  @media (max-width: ${({ theme }) => theme.breakpoints.lg}) {
    width: 100%;
    height: 100%;
  }
`;

const ChatWrapper = styled.div`
  width: 40%;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding-inline: 8px;

  @media (max-width: ${({ theme }) => theme.breakpoints.lg}) {
    width: 100%;
    height: auto;
    min-height: 150px;
    max-height: 50%;
    flex-direction: column;
    border-top: 1px solid ${({ theme }) => theme.colors.grey[700]};
    background-color: ${({ theme }) => theme.colors.black};
  }
`;

const ModalText = styled(DefaultText)`
  color: ${({ theme }) => theme.colors.white};
`;

const LiveShow: FC = () => {
  const [isWaitingForShowFinish, setIsWaitingForShowFinish] = useState(false);
  const [isMobileChatOpen, setIsMobileChatOpen] = useState(false);
  const {
    currentShowId,
    leaveAgoraChannel,
    onLeaveShowAsSpeakerOrListener,
    isJoined,
    isHost,
    onFinishShow,
    resetCurrentShowState,
  } = useAgoraEngine();
  const show = useShowDataById(currentShowId);
  const currentTopic = useTopicDataById(show?.currentTopicId);
  const { isAnonymous, user: currentUser } = useAuthUser();
  // const participant = useCurrentUserParticipantData(currentShowId);
  const participantRequest = useParticipantRequestToSpeak(currentShowId);
  const leftSpeakerId = useLeftTeamSpeakerId(currentShowId);
  const rightSpeakerId = useRightTeamSpeakerId(currentShowId);
  const leftSpeakerProfileId = useSpeakerDataById(leftSpeakerId)?.profileId;
  const rightSpeakerProfileId = useSpeakerDataById(rightSpeakerId)?.profileId;
  const isFinished = useMemo(
    () => show?.status === ShowStatus.Finished,
    [show?.status],
  );
  const amISpeaker = useMemo(
    () =>
      currentUser?.id === rightSpeakerProfileId ||
      currentUser?.id === leftSpeakerProfileId,
    [rightSpeakerProfileId, leftSpeakerProfileId, currentUser?.id],
  );
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { isModalShown: isExitModalShown, showModal, hideModal } = useModal();
  const { showAlert } = useAlert();
  const { isMobile, isTablet } = useScreenSizes();
  const { openShareModal } = useShareModal();

  const onEndShow = useCallback(async () => {
    if (currentShowId) {
      if (!isHost) {
        try {
          await leaveAgoraChannel();
          // TODO: remove this after we add the end show page
          resetCurrentShowState();
        } catch (error) {
          console.error('Error during leave operations:', error);
        }
      }
      showAlert(AlertType.Success, 'Show ended');
      navigate(APP_ROUTES.HOME, { replace: true });
    }
  }, [
    currentShowId,
    isHost,
    leaveAgoraChannel,
    resetCurrentShowState,
    showAlert,
    navigate,
  ]);

  const onLeaveShow = useCallback(() => {
    hideModal();

    if (isHost) {
      setIsWaitingForShowFinish(true);
      onFinishShow();
    } else {
      participantRequest &&
        dispatch(
          requestsToSpeakSlice.actions.setRequestToSpeakDataById({
            id: participantRequest?.id || '',
            status: RequestToSpeakStatus.Cancelled,
          }),
        );
      onLeaveShowAsSpeakerOrListener();
    }
  }, [
    hideModal,
    isHost,
    onFinishShow,
    participantRequest,
    dispatch,
    onLeaveShowAsSpeakerOrListener,
  ]);

  const setChatMessageData = useCallback(
    (chatMessageData: ChatMessageServerData, isSend = false) => {
      if (chatMessageData && currentUser?.id !== chatMessageData.user.id) {
        const { show: showData, user, ...message } = chatMessageData;
        dispatch(
          usersSlice.actions.setUserDataById({
            id: user.id,
            name: user.name,
            usernameColor: user.settings?.usernameColor,
          }),
        );
        dispatch(
          chatSlice.actions.setChatMessageById({
            ...message,
            showId: showData.id,
            userId: user.id,
          }),
        );

        if (isSend) {
          dispatch(
            chatSlice.actions.addMessageToShowIds({
              showId: showData.id,
              id: message.id,
            }),
          );
        }
      }
    },
    [dispatch, currentUser?.id],
  );

  const handleMobileChatToggle = useCallback(() => {
    setIsMobileChatOpen((prev) => !prev);
  }, []);

  const onSharePress = useCallback(() => {
    if (show) {
      const shareData = generateShowShareData({
        id: show.id,
        status: show.status,
        title: show.title,
        image: show.image,
      });
      openShareModal(shareData);
    }
  }, [show, openShareModal]);

  useEffect(() => {
    if (isFinished) {
      setIsWaitingForShowFinish(false);
      onEndShow();
    }
  }, [isFinished, onEndShow]);

  // TODO: Uncomment socket handlers for requests to speak and debates results
  // useEffect(() => {
  //   if (isCoHost) {
  //     return;
  //   }
  //   if (!isHost) {
  //     socket.on(
  //       `request-to-speak-update-${show?.id}-${currentUser?.id}`,
  //       (payload: RequestToSpeakSocketData) => {
  //         const { id, status } = payload;
  //         dispatch(
  //           requestsToSpeakSlice.actions.updateRequestToSpeakDataById({
  //             id,
  //             status,
  //           }),
  //         );
  //       },
  //     );
  //   } else {
  //     socket.on(
  //       `request-to-speak-${show?.id}`,
  //       (payload: RequestToSpeakSocketData) => {
  //         const { show: requestShow } = payload;
  //         if (requestShow.id === show?.id) {
  //           dispatch(
  //             requestsToSpeakSlice.actions.increaseRequestsToSpeakCount(
  //               payload.team,
  //             ),
  //           );
  //         }
  //       },
  //     );
  //   }
  //   return () => {
  //     if (isCoHost) {
  //       return;
  //     }
  //     if (!isHost) {
  //       socket.off(`request-to-speak-update-${show?.id}-${currentUser?.id}`);
  //     } else {
  //       socket.off(`request-to-speak-${show?.id}`);
  //     }
  //   };
  // }, [isHost, isCoHost, show?.id, currentUser?.id, dispatch]);

  // useEffect(() => {
  //   if (!isHost) {
  //     return;
  //   }

  //   socket.on(
  //     `cancel-request-to-speak-${show?.id}`,
  //     (payload: { id: string; show: { id: string }; side: TeamType }) => {
  //       const { show: requestShow } = payload;
  //       if (requestShow.id === show?.id) {
  //         dispatch(
  //           requestsToSpeakSlice.actions.decreaseRequestsToSpeakCount(
  //             payload.side,
  //           ),
  //         );
  //       }
  //     },
  //   );

  //   return () => {
  //     socket.off(`cancel-request-to-speak-${show?.id}`);
  //   };
  // }, [isHost, show?.id, dispatch]);

  // useEffect(() => {
  //   socket.on(
  //     `debates-winner-${show?.id}`,
  //     (payload: { profileId: string }) => {
  //       if (payload.profileId) {
  //         addAnimation(`${Animation.Winner}_${payload.profileId}`);
  //       }
  //     },
  //   );

  //   return () => {
  //     socket.off(`debates-winner-${show?.id}`);
  //   };
  // }, [show?.id, addAnimation]);

  useEffect(() => {
    socket.on(
      `send-chat-message-${show?.id}`,
      (payload: ChatMessageServerData) => {
        setChatMessageData(payload, true);
      },
    );

    return () => {
      socket.off(`send-chat-message-${show?.id}`);
    };
  }, [show?.id, setChatMessageData]);

  useEffect(() => {
    socket.on(
      `pin-chat-message-${show?.id}`,
      (payload: ChatMessageServerData) => {
        if (payload.pinnedBy === currentUser?.id) {
          return;
        }

        if (payload.pinned) {
          const { show: showData, user, ...message } = payload;
          dispatch(
            chatSlice.actions.setChatMessageById({
              ...message,
              showId: showData.id,
              userId: user.id,
            }),
          );
          dispatch(
            usersSlice.actions.setUserDataById({
              id: user.id,
              name: user.name,
              usernameColor: user.settings?.usernameColor,
            }),
          );
        }
        dispatch(
          chatSlice.actions.updatePinnedChatMessageByShowId({
            id: payload.pinned ? payload.id : null,
            showId: show?.id,
          }),
        );
      },
    );

    return () => {
      socket.off(`pin-chat-message-${show?.id}`);
    };
  }, [show?.id, dispatch, currentUser?.id]);

  if (!show?.id && !isJoined) {
    return <Loader size={'large'} />;
  }

  return (
    <Container>
      <Header $backgroundImage={show?.image || backgroundImage}>
        <MenuContainer>
          <Menu
            showId={currentShowId}
            exitButtonText={
              isHost ? 'End Show' : amISpeaker ? 'Leave as guest' : 'Leave'
            }
            onExitShow={showModal}
          />
        </MenuContainer>

        <TitleContainer>
          <Title>{show?.title || ''}</Title>

          <ShareButton onClick={onSharePress} />
        </TitleContainer>
      </Header>

      <Body>
        <MainContent>
          <Content>
            {show.type === ShowType.Sideless ? null : (
              <Sides
                blueSide={show.blueSide}
                redSide={show.redSide}
                isCard={false}
                leftVotes={currentTopic?.leftVotes}
                rightVotes={currentTopic?.rightVotes}
                textStyle={{
                  fontSize: isMobile
                    ? theme.fonts.fontSize.sm
                    : theme.fonts.fontSize.lg,
                  paddingBlock: '4px',
                }}
              />
            )}

            <Participants
              showId={currentShowId}
              leftSpeakerId={leftSpeakerId}
              rightSpeakerId={rightSpeakerId}
              speakingUIDs={{}}
              isStatusesShown
              isRecord={false}
            />
          </Content>

          <ChatWrapper>
            <DynamicChat
              showId={currentShowId || ''}
              isShownChatForm={!isAnonymous && !isMobile && !isTablet}
            />
          </ChatWrapper>
        </MainContent>
      </Body>

      <Modal
        isShown={isExitModalShown}
        onClose={hideModal}
        onConfirm={onLeaveShow}
      >
        <ModalText>
          Are you sure you want to {isHost ? 'end' : 'leave'} the show
          {amISpeaker ? 'as guest' : ''}?
        </ModalText>
      </Modal>

      <FullScreenLoader isLoading={isWaitingForShowFinish} />

      <MobileChat
        showId={currentShowId || ''}
        isOpen={isMobileChatOpen}
        onClose={handleMobileChatToggle}
      />

      <MobileBottomMenu
        showId={currentShowId || ''}
        onChatToggle={handleMobileChatToggle}
        isLiveShow
      />
    </Container>
  );
};

export default memo(LiveShow);
